import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { QRCodeSVG } from 'qrcode.react';
import { ShoppingBag, QrCode, Copy, Clock, CheckCircle, XCircle, Package, Eye, Loader2 } from 'lucide-react';

const statusCfg = {
  pendente: { label: 'Pendente', cls: 'bg-zinc-400/10 text-zinc-400 border-zinc-400/20', icon: Clock },
  aguardando_pagamento: { label: 'Aguard. Pgto', cls: 'bg-amber-400/10 text-amber-400 border-amber-400/20', icon: Clock },
  pago: { label: 'Pago', cls: 'bg-green-400/10 text-green-400 border-green-400/20', icon: CheckCircle },
  separando: { label: 'Separando', cls: 'bg-purple-400/10 text-purple-400 border-purple-400/20', icon: Package },
  pronto_retirada: { label: 'Pronto para retirada', cls: 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20', icon: Package },
  enviado: { label: 'Enviado', cls: 'bg-indigo-400/10 text-indigo-400 border-indigo-400/20', icon: Package },
  finalizado: { label: 'Finalizado', cls: 'bg-blue-400/10 text-blue-400 border-blue-400/20', icon: Package },
  cancelado: { label: 'Cancelado', cls: 'bg-red-400/10 text-red-400 border-red-400/20', icon: XCircle },
};
const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function AlunoPedidosPage() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pixDialog, setPixDialog] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [detailPedido, setDetailPedido] = useState(null);

  const fetchData = useCallback(async () => {
    try { const r = await api.get('/aluno/loja/pedidos'); setPedidos(r.data); }
    catch { toast.error('Erro ao carregar pedidos'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gerarPix = async (pedidoId) => {
    setPixLoading(true);
    try {
      const r = await api.post(`/aluno/loja/pedidos/${pedidoId}/gerar-pix`);
      setPixDialog(r.data);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    setPixLoading(false);
  };

  const copyPix = () => {
    if (pixDialog?.pix_code) { navigator.clipboard.writeText(pixDialog.pix_code); toast.success('Codigo PIX copiado!'); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48 bg-[#141414]" />{[1,2].map(i => <Skeleton key={i} className="h-24 bg-[#141414]" />)}</div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="aluno-pedidos-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">MEUS PEDIDOS</h1>

      {pedidos.length === 0 ? (
        <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-12 text-center">
          <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhum pedido realizado.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {pedidos.map(p => {
            const cfg = statusCfg[p.status] || statusCfg.pendente;
            const StatusIcon = cfg.icon;
            return (
              <Card key={p.id} className="bg-[#141414] border-[#27272A] hover:border-[#3f3f46] transition-all" data-testid={`aluno-pedido-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.cls.split(' ')[0]}`}>
                        <StatusIcon className={`w-4 h-4 ${cfg.cls.split(' ')[1]}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Pedido #{p.id.slice(0, 8)}</p>
                        <p className="text-xs text-zinc-500">{p.created_at?.split('T')[0]} | {p.itens?.length || 0} itens</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-lg font-bold font-['Barlow_Condensed'] text-white">{fmt(p.valor_total)}</p>
                      <Badge className={cfg.cls}>{cfg.label}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => setDetailPedido(p)} className="text-zinc-400 hover:text-white" data-testid={`view-pedido-${p.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {(p.status === 'pendente' || p.status === 'aguardando_pagamento') && (
                        <Button
                          size="sm"
                          onClick={() => p.pix_code ? setPixDialog({ pix_code: p.pix_code, valor: p.valor_total }) : gerarPix(p.id)}
                          disabled={pixLoading}
                          className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90"
                          data-testid={`pix-pedido-${p.id}`}
                        >
                          {pixLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4 mr-1" />}
                          {p.pix_code ? 'Ver PIX' : 'Pagar'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailPedido} onOpenChange={() => setDetailPedido(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">Pedido #{detailPedido?.id?.slice(0, 8)}</DialogTitle></DialogHeader>
          {detailPedido && (
            <div className="space-y-3" data-testid="aluno-pedido-detail">
              <div className="flex justify-between"><span className="text-zinc-400 text-sm">Status</span><Badge className={statusCfg[detailPedido.status]?.cls}>{statusCfg[detailPedido.status]?.label}</Badge></div>
              <div className="flex justify-between"><span className="text-zinc-400 text-sm">Data</span><span className="text-white text-sm">{detailPedido.created_at?.split('T')[0]}</span></div>
              {detailPedido.prazo_entrega_texto && (
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Prazo estimado</span>
                  <span className="text-blue-400 text-sm font-medium">
                    {detailPedido.prazo_entrega_texto}
                  </span>
                </div>
              )}
              {detailPedido.observacoes && <div><span className="text-zinc-400 text-sm">Obs:</span><p className="text-white text-sm mt-1">{detailPedido.observacoes}</p></div>}
              <div className="border-t border-[#27272A] pt-3">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Itens</p>
                {detailPedido.itens?.map((item, i) => (
                  <div key={i} className="flex justify-between py-1.5 border-b border-[#27272A] last:border-0">
                    <span className="text-sm text-white">{item.quantidade}x {item.nome}</span>
                    <span className="text-sm text-zinc-400">{fmt(item.subtotal)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#27272A]">
                  <span className="text-sm font-bold text-white">Total</span>
                  <span className="text-lg font-black font-['Barlow_Condensed'] text-white">{fmt(detailPedido.valor_total)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PIX Dialog */}
      <Dialog open={!!pixDialog} onOpenChange={() => setPixDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2"><QrCode className="w-5 h-5 text-[var(--cor-primaria,#007AFF)]" /> Pagar com PIX</DialogTitle></DialogHeader>
          {pixDialog && (
            <div className="space-y-4 text-center" data-testid="aluno-pedido-pix-dialog">
              <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">{fmt(pixDialog.valor)}</p>
              <div className="bg-white p-4 rounded-lg inline-block mx-auto"><QRCodeSVG value={pixDialog.pix_code} size={200} /></div>
              <div className="flex gap-2">
                <Input value={pixDialog.pix_code} readOnly className="bg-[#0A0A0A] border-[#27272A] text-xs" data-testid="pedido-pix-code-input" />
                <Button size="icon" variant="outline" onClick={copyPix} className="border-[#27272A] shrink-0" data-testid="pedido-copy-pix-btn"><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-zinc-500">Aguardando confirmacao manual do pagamento pela escola.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
